import { Button, Heading, InputPhone, Modal, Text, DEFAULT_CONFIG as AllowedCountries } from '@afterpaytouch/core'
import { ErrorCode, isFetchBaseQueryError, isPortalApiError } from '@afterpaytouch/portal-api'
import { SerializedError } from '@reduxjs/toolkit'
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query'
import { useTranslation } from 'next-i18next'
import React, { ChangeEvent, FunctionComponent, useContext } from 'react'
import { useConsumerCountry } from '../../../../state'
import { useChangeMobileVerifyMobileMutation } from '../../../../state/account/changeMobile'
import { EditMobileNumberModalContext, EditMobileNumberSteps } from '../../EditMobileNumberModal'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'

const I18N_NAMESPACE = ['common', 'account']

const getErrorText = (isErr: boolean, err: FetchBaseQueryError | SerializedError, errors: Partial<Record<ErrorCode, string>>, fallback: string): string => {
  if (!isErr) {
    return ''
  }
  if (!isFetchBaseQueryError(err)) {
    return fallback
  }
  if (!isPortalApiError(err.data)) {
    return fallback
  }
  return errors[err.data?.errorCode] ?? fallback
}

export const Edit: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const { token, setStep, newMobile, setNewMobile } = useContext(EditMobileNumberModalContext)

  const testNameSpace = 'edit-mobile-number-modal-edit'

  const onMobileNumberChange = (e: ChangeEvent<HTMLInputElement>, rawValue: string): void => setNewMobile(rawValue)

  const [verifyMobile, { error: verifyMobileError, isError: isVerifyMobileError, isLoading: isVerifyMobileLoading }] = useChangeMobileVerifyMobileMutation()

  const onButtonClick = async (): Promise<void> => {
    try {
      await verifyMobile({
        body: {
          newMobile,
          token,
        },
      }).unwrap()
      setStep(EditMobileNumberSteps.Confirm)
    } catch (err) {}
  }

  const isMobileInputDisabled = isVerifyMobileLoading
  const isButtonDisabled = isVerifyMobileLoading || newMobile.length === 0

  const verifyMobileErrors: Partial<Record<ErrorCode, string>> = {
    [ErrorCode.InvalidPhoneNumber]: t('account:edit:mobile:edit:content:mobile-field:error:invalid_phone_number'),
    [ErrorCode.ExistingPhoneNumber]: t('account:edit:mobile:edit:content:mobile-field:error:existing_phone_number'),
  }

  const consumerCountry = useConsumerCountry()

  return (
    <>
      <Modal.Header divider>
        <div className='text-center'>
          <Heading testNameSpace={testNameSpace} size='L'>
            {t('account:edit:mobile:edit:header:heading')}
          </Heading>
        </div>
      </Modal.Header>
      <Modal.Content>
        <div>
          <div className='mt-2'>
            <Text>{t('account:edit:mobile:edit:content:message')}</Text>
          </div>
          <MaskedElement>
            <div className='mt-4'>
              <InputPhone
                onChange={onMobileNumberChange}
                label={t('account:edit:mobile:edit:content:mobile-field:placeholder')}
                disabled={isMobileInputDisabled}
                error={isVerifyMobileError}
                errorText={getErrorText(
                  isVerifyMobileError,
                  // @ts-ignore: OPERATION BLEED STOPPER
                  verifyMobileError,
                  verifyMobileErrors,
                  t('account:edit:mobile:edit:content:mobile-field:error:generic')
                )}
                testNameSpace={testNameSpace}
                autoFocus
                selectedCountry={consumerCountry}
                singleCountryShowEmoji
                allowedCountries={[AllowedCountries.find((config) => config.countryCode === consumerCountry) ?? AllowedCountries[0]]}
              />
            </div>
          </MaskedElement>
          <div className='mt-6'>
            <Button padding='Fluid' disabled={isButtonDisabled} loading={isVerifyMobileLoading} onClick={onButtonClick} testNameSpace='edit-mobile-continue'>
              <Text bold>{t('account:edit:mobile:edit:content:cta')}</Text>
            </Button>
          </div>
        </div>
      </Modal.Content>
    </>
  )
}
